// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import type { StoreSpecial as V3StoreSpecial } from '@iheartjane/data-access';
import { ALL_STORE_SPECIALS_OPTS } from '@iheartjane/data-access';
import { useStoreSpecials } from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import {
  getSpecialTagLabel,
  hideStrikethroughPrice,
  isEffectsDisabledState,
} from '@iheartjane/menu';
import {
  Box,
  Flex,
  ReadMore,
  Typography,
  useMobileMediaQuery,
  useToast,
} from '@iheartjane/reefer';
import type { PriceId } from '@iheartjane/types';
import { formatCurrency } from '@iheartjane/utils';
import isEmpty from 'lodash-es/isEmpty';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import {
  useLocation,
  useMatches,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router';

import { StoreSpecialPdpCallout } from '@jane/kiosk/components/StoreSpecialPdpCallout/StoreSpecialPdpCallout';
import { makeCurrentSort, reviewsSortDefault } from '@jane/search/types';
import type { CurrentSort } from '@jane/search/types';
import { Carousel } from '@jane/shared-ecomm/components';
import { useGetStoreSpecials } from '@jane/shared-ecomm/data-access';
import { useSDK } from '@jane/shared-ecomm/providers';
import { getSortedTerpenes, getTopFlavors } from '@jane/shared-ecomm/util';
import { useGetStore } from '@jane/shared/data-access';
import { useNavigateBack } from '@jane/shared/hooks';
// eslint-disable-next-line no-restricted-imports
import type { BrandSpecial } from '@jane/shared/models';
import type { LabResult, Store, StoreSpecial } from '@jane/shared/types';
import {
  availableWeightsForMenuProduct,
  calculateDiscountPercentage,
  getIfAllPriceIdsShareDiscount,
  getMaxCartQuantity,
  getMenuPdpApplicableGoldTagLabel,
  getMenuRowWeightFilter,
  getProductImages,
  getWeightFilterForFeaturedCategory,
  labelForWeightId,
  lowestPriceForMenuProduct,
} from '@jane/shared/util';

import {
  DealTag,
  FeelingSection,
  FlavorSection,
  InfiniteScrollThreshold,
  JaneGoldCallout,
  Modal,
  ModalHeader,
  PdpRow,
  ProductAddedToast,
  ProductReviewsSection,
  ProductTags,
  ProductWeightsGrid,
  QuantityButton,
  StarRating,
  TerpeneSection,
} from '../../components';
import type { RootLoaderType } from '../../data-access';
import { useGetCart, useProductReviews } from '../../data-access';
import {
  useAddProductOnSuccess,
  useAddProductToBag,
  useGetProductPrices,
  useIpadMediaQuery,
  useKioskSpacing,
  usePdpModalClose,
  useRowContext,
  useShouldKioskShowGold,
  useTypedRouteLoaderData,
} from '../../hooks';
import {
  useBrandSpecials,
  useProductDetails,
  useProductTracking,
} from '../../providers';
import type { OptionalMenuRouteParams } from '../../types';
import { isPriceId } from '../../types';
import {
  capitalize,
  customSubcategoryLabel,
  getApplicableSpecialForProductPriceId,
  getSource,
  isSupportedStoreSpecial,
} from '../../util';
import { trackCashBackPromptTap } from '../../util/lib/tracking/track';

const ReadMoreContainer = styled(Flex)(() => {
  const isIpad = useIpadMediaQuery();

  return {
    fontSize: isIpad ? 16 : 22,
  };
});

const imageSize = '360px';

export const ProductDetail = () => {
  /** ----- Initial data, state + hooks ----- */
  const {
    product,
    productId,
    storeId,
    feelings,
    isFeelingsLoading,
    pdpRowInstance,
    isPdpRowLoading,
  } = useProductDetails();
  const [currentSort, setCurrentSort] = useState<CurrentSort>(
    makeCurrentSort(reviewsSortDefault)
  );
  const scrollRef = useRef<HTMLDivElement>(null);
  const [quantity, setQuantity] = useState<number>(1);

  const matches = useMatches();
  const navigate = useNavigate();
  const toast = useToast();
  const isMobile = useMobileMediaQuery({});
  const { inView, ref } = useInView();
  const { pathname } = useLocation();
  const { specialId } = useParams<OptionalMenuRouteParams>();
  const [queryParams] = useSearchParams();
  const { defaultSpacing, getKioskSpacing, isIpad } = useKioskSpacing();
  const { onModalClose, pdpDepth } = usePdpModalClose();
  const { data: cart } = useGetCart();
  const { brandSpecials } = useBrandSpecials();
  const {
    cartToppers: { addCartTopperId },
    inlineAds: { addInlineAdId },
  } = useProductTracking();

  const specialRedesign = useFlag(FLAGS.c3SpecialsRedesignMenu);

  /** ----- Data fetching ----- */
  const { store: initialStore } =
    useTypedRouteLoaderData<RootLoaderType>('root');
  const { data: store } = useGetStore<Store>(storeId, {
    initialData: initialStore,
  });
  const { pdp_setting } = store;

  const { rowId, featuredCategory } = useRowContext(store);
  const menuRowWeightFilter = useMemo(
    () =>
      rowId
        ? getMenuRowWeightFilter({
            customRowSettings: store.custom_row_settings,
            rowId,
          })
        : getWeightFilterForFeaturedCategory(store, featuredCategory),
    [store, featuredCategory, rowId]
  );

  const { sdk } = useSDK();

  const weights = availableWeightsForMenuProduct(product);
  const priceId = useMemo(() => {
    const priceParam = queryParams.get('priceId');
    if (isPriceId(priceParam)) {
      return weights.includes(priceParam) ? priceParam : null;
    }
    return null;
  }, [queryParams, weights]);
  const { images: productImages, stockPhoto } = useMemo(
    () => getProductImages(product),
    [product]
  );
  const [selectedWeight, setSelectedWeight] = useState<PriceId>(
    priceId || lowestPriceForMenuProduct(product, weights) || 'each'
  );

  /** Specials Info */
  const useV3 = useFlag(FLAGS.c3UseV3StoreSpecials);
  const { data: v1Specials = [] } = useGetStoreSpecials(storeId, {
    enabled: !useV3 && !!storeId,
    sort: 'schedule',
  });
  const v3SpecialsQuery = useStoreSpecials({
    ...ALL_STORE_SPECIALS_OPTS,
    enabled: !!useV3 && !!storeId,
    storeId,
  });
  const specials = useMemo<(StoreSpecial | V3StoreSpecial)[]>(() => {
    if (useV3) {
      return v3SpecialsQuery.data?.pages.flatMap((page) => page.specials) ?? [];
    }
    return v1Specials;
  }, [useV3, v3SpecialsQuery.data, v1Specials]);

  const supportedStoreSpecialIds = specials
    .filter((special) => isSupportedStoreSpecial(special, true))
    .map(({ id }) => id);
  const productSpecialId = specialId || product.special_id?.toString();
  const applicableSpecial = getApplicableSpecialForProductPriceId(
    product,
    specials,
    selectedWeight,
    supportedStoreSpecialIds
  );
  const specialInScope = specials.find(
    ({ id }) => id.toString() === productSpecialId
  );
  const specialTagLabel = getSpecialTagLabel({
    defaultWeight: selectedWeight,
    product,
    store,
  });

  const shouldShowGold = useShouldKioskShowGold({ product });

  /** Brand Special Info */
  const sortedBrandSpecials: BrandSpecial[] = useMemo(
    () =>
      (product.applicable_brand_special_ids ?? []).flatMap((id) => {
        const special = brandSpecials.find((special) => special.id === id);
        return special ? [special] : [];
      }),
    [product.applicable_brand_special_ids, brandSpecials]
  );

  // Determines if single tag is shown, or individual tags for each weight
  const allPricesShareDiscount = useMemo(
    () =>
      shouldShowGold &&
      getIfAllPriceIdsShareDiscount({
        menuProduct: product,
      }),
    [product, shouldShowGold]
  );

  // If all weights share the same discount
  const janeGoldTagLabel = useMemo(() => {
    if (!shouldShowGold) return null;

    return getMenuPdpApplicableGoldTagLabel({
      brandSpecials: sortedBrandSpecials,
      menuProduct: product,
    });
  }, [sortedBrandSpecials, product, shouldShowGold]);

  /** Price Info */
  const getProductPrice = useGetProductPrices(product, specials);
  const { unitPrice, discountPrice, price } = getProductPrice(selectedWeight);
  const showOriginalPrice =
    !!discountPrice &&
    unitPrice !== discountPrice &&
    !hideStrikethroughPrice(store?.state || '');
  const discountPercentage = useMemo(() => {
    return calculateDiscountPercentage(unitPrice, discountPrice);
  }, [unitPrice, discountPrice]);

  /** Reviews Info */
  const {
    data: productReviews,
    isLoading: isReviewsLoading,
    hasNextPage,
    fetchNextPage,
  } = useProductReviews(productId, { currentSort });
  const showReviews =
    store.product_reviews_enabled && (product.review_count ?? 0) >= 5;
  const isReviewsSectionVisible = useMemo(
    () => isReviewsLoading || (productReviews?.pages[0].hits ?? []).length > 0,
    [isReviewsLoading, productReviews?.pages]
  );

  /** ----- UseEffects ----- */
  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, inView]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo(0, 0);
    }
  }, [productId]);

  /** ----- Memos/Variables ----- */
  const inventoryCount = getMaxCartQuantity(product, selectedWeight);
  const almostGone = inventoryCount < 4;

  const isTopper = useMemo(
    () => queryParams.get('topper')?.toLowerCase() === 'true',
    [queryParams]
  );

  const isInlineAd = useMemo(
    () => queryParams.get('inline')?.toLowerCase() === 'true',
    [queryParams]
  );

  const reviews = useMemo(
    () => productReviews?.pages.flatMap(({ hits }) => hits) ?? [],
    [productReviews]
  );

  const topFlavors = useMemo(
    () => getTopFlavors(product.lab_results as LabResult[], selectedWeight),
    [product.lab_results, selectedWeight]
  );

  const sortedTerpenes = useMemo(
    () => getSortedTerpenes(product.lab_results as LabResult[], selectedWeight),
    [product.lab_results, selectedWeight]
  );

  const existingCartProduct = useMemo(() => {
    return (cart?.products ?? []).find(
      ({ price_id, product_id }) =>
        product.product_id === product_id && price_id === selectedWeight
    );
  }, [cart, product, selectedWeight]);

  const isFeelingsSectionVisible = useMemo(
    () =>
      store.product_reviews_enabled &&
      !store.pdp_setting?.hide_effects &&
      !isEffectsDisabledState(store.state) &&
      (isFeelingsLoading || feelings.length > 0),
    [
      isFeelingsLoading,
      feelings,
      store.pdp_setting?.hide_effects,
      store.product_reviews_enabled,
      store.state,
    ]
  );

  /** ----- Functions ----- */
  const goBack = useNavigateBack(`/stores/${storeId}/menu/all`);
  const trackDmProducts = useCallback(() => {
    if (isInlineAd) {
      addInlineAdId(product.product_id);
    }
    if (isTopper) {
      addCartTopperId(product.product_id);
    }
  }, [
    addCartTopperId,
    addInlineAdId,
    isInlineAd,
    isTopper,
    product.product_id,
  ]);

  const onSuccess = useAddProductOnSuccess({
    callback: trackDmProducts,
    cart,
    discountPercentage,
    discountPrice,
    product,
    quantity,
    selectedWeight,
    source: getSource({ pathname, matches, isTopper }),
    store,
    unitPrice,
    isInlineAd,
    isTopper,
    specialInScope,
  });

  const { onAddToBag } = useAddProductToBag({
    cart,
    onSuccess,
    priceId: selectedWeight,
    product,
    store,
  });

  const onAddToBagClick = useCallback(
    async (quantity?: number) => {
      sdk?.sendSessionEvent({
        events: [{ type: 'clickedAddToCart', storeId, productId }],
      });
      await onAddToBag({ quantity });
      if (pathname.includes('cart')) {
        toast.add({
          label: (
            <ProductAddedToast productCount={quantity} storeId={storeId} />
          ),
        });
      }

      goBack(Number(pdpDepth));
    },
    [sdk, storeId, productId, onAddToBag, pathname, goBack, pdpDepth, toast]
  );

  const buildProductPrice = useCallback(
    (weight: PriceId) => {
      const weightLabel = labelForWeightId(weight, product.amount);
      const weightPrice = formatCurrency(getProductPrice(weight).price);

      return `${weightPrice} / ${weightLabel}`;
    },
    [getProductPrice, product.amount]
  );

  const onBrandSpecialClick = (brandSpecial: BrandSpecial) => {
    trackCashBackPromptTap({ brandSpecial, source: ['PDP'] });

    navigate(`/stores/${store.id}/menu/jane-gold/${brandSpecial.id}`);
  };

  return (
    <Modal
      appId="root"
      contentLabel="Product Detail"
      onRequestClose={onModalClose}
      open
      overflow="scroll"
      overlayClose
    >
      <ModalHeader nested={Number(pdpDepth) > 1} onRequestClose={onModalClose}>
        <Flex alignItems="center" grow={1} justifyContent="space-between">
          <Typography variant={isIpad ? 'header-bold' : 'title-bold'}>
            Product details
          </Typography>
          <QuantityButton
            quantity={quantity}
            setQuantity={setQuantity}
            existingProductQty={existingCartProduct?.count}
            maxProductQty={getMaxCartQuantity(product, selectedWeight)}
            onAddToBagClick={onAddToBagClick}
          />
        </Flex>
      </ModalHeader>
      <Modal.Content padding={false} ref={scrollRef}>
        <Flex flexDirection="column" p={defaultSpacing}>
          <Flex flexDirection="row" mb={defaultSpacing}>
            <Flex position="relative">
              <Carousel
                items={productImages}
                desktopHeight={imageSize}
                desktopWidth={imageSize}
                forceMobile
                mobileHeight={imageSize}
                mobileWidth={imageSize}
                stockPhoto={stockPhoto}
                showThumbnails={false}
                responsive
                withLightbox={false}
              />
            </Flex>
            <Flex
              flexDirection="column"
              pl={32}
              maxWidth={`calc(100% - ${imageSize})`}
            >
              {(!!janeGoldTagLabel || !!discountPrice) && (
                <Flex flexDirection="row" gap={8} mb={8}>
                  {!!janeGoldTagLabel && (
                    <DealTag tagLabel={janeGoldTagLabel} isGold />
                  )}
                  {!!discountPrice && <DealTag tagLabel={specialTagLabel} />}
                </Flex>
              )}
              {!!product.brand && (
                <Typography mb={8} color="grays-black" variant="body">
                  {product.brand}
                </Typography>
              )}
              <Typography mb={8} color="grays-mid" variant="body">
                {store?.custom_product_type_labels?.[product.kind] ??
                  capitalize(product.kind)}{' '}
                /{' '}
                {!isEmpty(product.brand_subtype)
                  ? product.brand_subtype
                  : customSubcategoryLabel({
                      store,
                      subcategory: {
                        label: String(product.root_subtype),
                        value: `${product.kind}:${product.root_subtype}`,
                      },
                    })}
              </Typography>
              <Typography
                mb={8}
                variant={isIpad ? 'header-bold' : 'title-bold'}
              >
                {product.name}
              </Typography>
              {applicableSpecial && applicableSpecial.title && (
                <Typography color="sunset" maxLines={1}>
                  {applicableSpecial.title}
                </Typography>
              )}
              <Typography
                mb={showOriginalPrice ? undefined : getKioskSpacing(32, 16)}
                color={discountPrice ? 'sunset' : undefined}
                variant={isIpad ? 'header' : 'title'}
              >
                {weights.length === 1 && !product.amount
                  ? buildProductPrice(weights[0])
                  : formatCurrency(price)}
              </Typography>
              {showOriginalPrice && (
                <Typography
                  mb={getKioskSpacing(32, 16)}
                  color="grays-mid"
                  variant="body"
                  strikeThrough
                >
                  {formatCurrency(unitPrice)}
                </Typography>
              )}
              {!!product && (
                <ProductTags
                  store={store}
                  product={product}
                  selectedWeight={selectedWeight}
                />
              )}
              {showReviews && (
                <StarRating
                  aggregateRating={product.aggregate_rating}
                  isPdp
                  reviewCount={product.review_count ?? undefined}
                />
              )}
              {almostGone && (
                <Typography color="grays-mid" variant="body">
                  Only {inventoryCount} left
                </Typography>
              )}
            </Flex>
          </Flex>
          {sortedBrandSpecials.length > 0 && (
            <JaneGoldCallout
              variant="pdp"
              brandSpecials={sortedBrandSpecials}
              cartBrandDiscounts={cart?.brand_discounts ?? []}
              cartUuid={cart?.uuid ?? ''}
              onClick={(brandSpecial) =>
                onBrandSpecialClick(brandSpecial as any)
              }
            />
          )}
          {specialRedesign && specials.length && (
            <StoreSpecialPdpCallout
              cart={cart}
              specials={specials}
              product={product}
              store={store}
            />
          )}
          {weights.length > 1 && (
            <Box borderRadius="lg" mb={24}>
              <Typography mb={getKioskSpacing(24, 16)} variant="body-bold">
                Select size
              </Typography>
              <ProductWeightsGrid
                allPricesShareDiscount={allPricesShareDiscount}
                onWeightClick={setSelectedWeight}
                product={product}
                selectedWeight={selectedWeight}
                shouldShowGold={shouldShowGold}
                specials={specials}
                weightFilter={menuRowWeightFilter}
                weightRuleBrandSpecial={
                  janeGoldTagLabel ? undefined : sortedBrandSpecials
                }
              />
            </Box>
          )}
          {(product.store_notes || product.description) &&
            !pdp_setting?.hide_description && (
              <Box
                border="transparent-black-five"
                borderRadius="lg"
                p={defaultSpacing}
                mb={24}
              >
                <Typography mb={getKioskSpacing(24, 8)} variant="body-bold">
                  Description
                </Typography>
                <ReadMoreContainer>
                  <ReadMore
                    characters={isMobile ? 100 : 300}
                    content={product.store_notes ?? product.description}
                    more="Show more"
                  />
                </ReadMoreContainer>
              </Box>
            )}
          {isFeelingsSectionVisible && (
            <FeelingSection
              disabledFeelings={store.disabled_feeling_and_activity_tags}
              isLoading={isFeelingsLoading}
              feelings={feelings}
            />
          )}
          {topFlavors.length > 0 && <FlavorSection flavors={topFlavors} />}
          {sortedTerpenes.length > 0 && (
            <TerpeneSection sortedTerpenes={sortedTerpenes} />
          )}
          <PdpRow isLoading={isPdpRowLoading} row={pdpRowInstance} />
          {showReviews && isReviewsSectionVisible && (
            <ProductReviewsSection
              productReviews={reviews}
              product={product}
              isLoading={isReviewsLoading}
              currentSort={currentSort}
              setCurrentSort={setCurrentSort}
            >
              <InfiniteScrollThreshold ref={ref} />
            </ProductReviewsSection>
          )}
        </Flex>
      </Modal.Content>
    </Modal>
  );
};
